import React, { useState, useEffect, useCallback } from 'react';
import { Zap, Clock, Crown, ShoppingCart, Star, ArrowRight, Shield, Lock, Timer, Trophy, Flame } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import OptimizedImage from './OptimizedImage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CountdownTimer = ({ endTime, label = 'Ends in', compact = false }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calc = () => {
      const diff = new Date(endTime) - new Date();
      if (diff > 0) {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((diff / 1000 / 60) % 60),
          seconds: Math.floor((diff / 1000) % 60),
        });
      }
    };
    calc();
    const timer = setInterval(calc, 1000);
    return () => clearInterval(timer);
  }, [endTime]);

  if (compact) {
    return (
      <span className="text-sm font-mono font-bold">
        {timeLeft.hours.toString().padStart(2, '0')}:{timeLeft.minutes.toString().padStart(2, '0')}:{timeLeft.seconds.toString().padStart(2, '0')}
      </span>
    );
  }

  const TimeBox = ({ value, unit }) => (
    <div className="flex flex-col items-center">
      <div className="bg-white/20 backdrop-blur-sm text-white font-bold text-lg sm:text-xl px-2.5 py-1.5 rounded-lg min-w-[44px] text-center font-mono">
        {String(value).padStart(2, '0')}
      </div>
      <span className="text-[10px] text-white/70 mt-0.5 uppercase">{unit}</span>
    </div>
  );

  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-white/80 font-medium mr-1 hidden sm:inline">{label}</span>
      {timeLeft.days > 0 && <><TimeBox value={timeLeft.days} unit="D" /><span className="text-white/50 font-bold">:</span></>}
      <TimeBox value={timeLeft.hours} unit="H" />
      <span className="text-white/50 font-bold">:</span>
      <TimeBox value={timeLeft.minutes} unit="M" />
      <span className="text-white/50 font-bold">:</span>
      <TimeBox value={timeLeft.seconds} unit="S" />
    </div>
  );
};

const FlashDealCard = ({ product, discount, onAddToCart, onQuickView, dealId }) => {
  const savings = (product.original_price - product.flash_price).toFixed(2);

  const handleQuickView = () => {
    onQuickView?.({
      ...product,
      price: product.flash_price || product.sale_price || product.price,
      originalPrice: product.original_price || product.price,
      image: product.image || (product.images && product.images[0]) || '',
      images: product.images || [],
      inStock: (product.stock_quantity || 0) > 0,
      rating: product.rating || 4.5,
      reviewCount: product.review_count || 0,
      discount: discount,
    });
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all group border border-red-100 dark:border-red-900/30" data-testid={`flash-deal-product-${product.id}`}>
      {/* Image - clickable to open QuickView */}
      <div className="relative aspect-square overflow-hidden bg-gray-50 dark:bg-gray-900 cursor-pointer" onClick={handleQuickView}>
        <OptimizedImage
          src={product.image || (product.images && product.images[0]) || 'https://via.placeholder.com/200'}
          alt={product.name}
          className="w-full h-full group-hover:scale-110 transition-transform duration-500"
        />
        {/* Discount Badge */}
        <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-lg shadow-lg">
          -{discount}%
        </div>
        {/* Savings */}
        <div className="absolute bottom-2 right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-lg">
          Save ${savings}
        </div>
      </div>

      {/* Info */}
      <div className="p-3">
        <h4 className="text-sm font-medium text-gray-800 dark:text-white line-clamp-2 min-h-[36px] group-hover:text-red-600 transition-colors cursor-pointer" onClick={handleQuickView}>
          {product.name}
        </h4>

        {/* Rating */}
        {product.rating && (
          <div className="flex items-center gap-1 mt-1">
            <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
            <span className="text-xs text-gray-500">{product.rating}</span>
          </div>
        )}

        {/* Prices */}
        <div className="flex items-center gap-2 mt-2">
          <span className="text-lg font-bold text-red-600">${product.flash_price?.toFixed(2)}</span>
          <span className="text-sm text-gray-400 line-through">${product.original_price?.toFixed(2)}</span>
        </div>

        {/* Stock indicator */}
        {product.stock_quantity !== undefined && (
          <div className="mt-2">
            <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${product.stock_quantity < 5 ? 'bg-red-500' : product.stock_quantity < 20 ? 'bg-orange-500' : 'bg-green-500'}`}
                style={{ width: `${Math.min(product.stock_quantity * 5, 100)}%` }}
              />
            </div>
            <p className={`text-[10px] mt-0.5 font-medium ${product.stock_quantity < 5 ? 'text-red-500' : 'text-gray-400'}`}>
              {product.stock_quantity < 5 ? `Only ${product.stock_quantity} left!` : `${product.stock_quantity} in stock`}
            </p>
          </div>
        )}

        {/* Add to Cart */}
        <button
          onClick={() => onAddToCart?.({
            ...product,
            price: product.flash_price || product.sale_price || product.price,
            originalPrice: product.original_price || product.price,
            image: product.image || (product.images && product.images[0]) || '',
          })}
          className="w-full mt-2 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-medium rounded-lg hover:from-red-600 hover:to-orange-600 transition-all flex items-center justify-center gap-1.5"
          data-testid={`flash-add-cart-${product.id}`}
        >
          <ShoppingCart className="w-3.5 h-3.5" />
          Add to Cart
        </button>
      </div>
    </div>
  );
};

const FlashDealsSection = ({ onAddToCart, onQuickView }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDeals = useCallback(async () => {
    try {
      const url = user?.id
        ? `${API_URL}/api/flash-deals/active?customer_id=${user.id}`
        : `${API_URL}/api/flash-deals/active`;
      const res = await fetch(url);
      if (res.ok) setData(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [user?.id]);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  if (loading) return null;
  if (!data?.deals?.length) return null;

  const currentDeal = data.deals[0];
  const isEarlyAccess = currentDeal.is_early_access;

  return (
    <section className="py-8" id="flash-deals" data-testid="flash-deals-section">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className={`rounded-2xl overflow-hidden mb-6 ${
          isEarlyAccess
            ? 'bg-gradient-to-r from-purple-600 via-indigo-600 to-violet-600'
            : 'bg-gradient-to-r from-red-500 via-orange-500 to-red-500'
        }`}>
          <div className="p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Left */}
              <div className="flex items-center gap-3 text-white">
                <div className={`p-2.5 rounded-full ${isEarlyAccess ? 'bg-white/20' : 'bg-white/20 animate-pulse'}`}>
                  {isEarlyAccess ? <Crown className="w-6 h-6 text-yellow-300" /> : <Zap className="w-6 h-6" />}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-bold text-xl">{currentDeal.title}</h2>
                    {isEarlyAccess && (
                      <span className="px-2 py-0.5 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full flex items-center gap-1" data-testid="early-access-badge">
                        <Lock className="w-3 h-3" /> EARLY ACCESS
                      </span>
                    )}
                  </div>
                  <p className="text-white/80 text-sm mt-0.5">
                    {isEarlyAccess
                      ? `Exclusive access for Top ${data.user_rank} streakers! Goes public in ${data.early_access_minutes} min.`
                      : currentDeal.description || `Up to ${currentDeal.discount_percent}% OFF on selected items!`
                    }
                  </p>
                </div>
              </div>

              {/* Right - Countdown */}
              <CountdownTimer endTime={currentDeal.ends_at} label={isEarlyAccess ? 'Early access for' : 'Ends in'} />
            </div>

            {/* Early Access Benefits Bar */}
            {data.has_early_access && (
              <div className="mt-4 pt-3 border-t border-white/20 flex flex-wrap items-center gap-4 text-white/90 text-xs">
                <span className="flex items-center gap-1">
                  <Trophy className="w-3.5 h-3.5 text-yellow-300" />
                  Rank #{data.user_rank} Monthly Streaker
                </span>
                <span className="flex items-center gap-1">
                  <Timer className="w-3.5 h-3.5" />
                  {data.early_access_minutes} min head start
                </span>
                <span className="flex items-center gap-1">
                  <Shield className="w-3.5 h-3.5" />
                  Priority stock access
                </span>
                <span className="flex items-center gap-1">
                  <Flame className="w-3.5 h-3.5 text-orange-300" />
                  Keep your streak to maintain access!
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Deals Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
          {currentDeal.products?.map((product) => (
            <FlashDealCard
              key={product.id}
              product={product}
              discount={currentDeal.discount_percent}
              dealId={currentDeal.id}
              onAddToCart={onAddToCart}
              onQuickView={onQuickView}
            />
          ))}
        </div>

        {/* Non-logged-in CTA */}
        {!user && (
          <div className="mt-6 p-4 bg-purple-50 dark:bg-purple-900/10 rounded-xl border border-purple-200 dark:border-purple-800 text-center">
            <p className="text-sm text-purple-700 dark:text-purple-400">
              <Crown className="w-4 h-4 inline mr-1 text-yellow-500" />
              <strong>Want early access?</strong> Sign in and check in daily to climb the streak leaderboard. Top 3 streakers get {data?.early_access_minutes || 30}-minute early access to flash deals!
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export { CountdownTimer, FlashDealCard };
export default FlashDealsSection;
